<script lang="ts">
  import ActionBox from "@kontextwerk/web-sdk/ui/layout/ActionBox.svelte"
  import SectionTitle from "@kontextwerk/web-sdk/ui/typography/SectionTitle.svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import Icon from "@kontextwerk/web-sdk/ui/primitives/Icon.svelte"
  import Input from "@kontextwerk/web-sdk/ui/forms/Input.svelte"
  import UserTabState from "@kontextwerk/control-web-frame/shared/modules/user-tab-state"
  import { mdiContentSave, mdiRestore } from "@mdi/js"
  import PermissionsGrid from "../../../../_shared/_modules/permissions-grid"
  import { PERMISSION_SECTIONS } from "../../../../_shared/_logic/permission-sections"
  import type { UserViewFrameContext } from "../../_shared/_logic/user-types"
  import { changeMembershipAccess } from "./_actions/changeMembershipAccess"
  import { roleGrantSections, roleGrantState } from "./_logic/roleGrants"

  const { viewFrameContext, activeTab } = $props<{
    viewFrameContext: UserViewFrameContext
    activeTab: string
  }>()

  const STATUS_OPTIONS = [
    { value: "active", name: "Aktiv" },
    { value: "suspended", name: "Gesperrt" },
  ]

  const access = $derived(viewFrameContext.memberAccess)
  const tabMeta = $derived.by(() => {
    if (activeTab === "general")
      return {
        title: "Allgemeine Berechtigungen",
        helpDialogId: "userPermissionsGeneral",
        helpDialogTooltip: "So funktionieren allgemeine Berechtigungen",
      }

    const section = PERMISSION_SECTIONS.find((candidate) => candidate.id === activeTab)

    return {
      title: section ? `${section.title} Berechtigungen` : "Rolle und Berechtigungen",
      helpDialogId: section?.helpDialogId ?? undefined,
      helpDialogTooltip: section
        ? (section.helpDialogTooltip ?? `So funktionieren ${section.title}-Berechtigungen`)
        : "So funktionieren Rollen und Berechtigungen",
    }
  })
  const roleTooltip = $derived(
    access?.self
      ? "Ihre eigene Rolle und Ihren Status kann nur eine andere berechtigte Person ändern."
      : access?.isOwnerMembership
        ? "Die Rolle „Inhaber“ wird nur durch eine Übertragung der Inhaberschaft geändert."
        : "Die Rolle legt die Berechtigungen dieser Mitgliedschaft fest. Änderungen werden erst nach „Speichern“ wirksam.",
  )
  const roleState = $derived(access?.selectedRole ?? null)
  const grants = $derived(roleState?.role?.grants ?? [])
  const sections = $derived(roleGrantSections(activeTab, grants))
  const grid = $derived(roleGrantState(grants))
</script>

{#if !access}
  <UserTabState
    loading={viewFrameContext.loading ||
      (Boolean(viewFrameContext.member) && !viewFrameContext.error)}
    error={viewFrameContext.error}
    userPresent={Boolean(viewFrameContext.member)}
    onRetry={viewFrameContext.onRetry ?? null}
    loadingLabel="Rolle wird geladen…"
  />
{:else if access.status === "loading" || access.status === "invalidated"}
  <UserTabState loading={true} loadingLabel="Rolle wird geladen…" />
{:else if access.status === "error"}
  <UserTabState
    error="Rolle und Status konnten nicht geladen werden."
    userPresent={true}
    onRetry={() => void access.load()}
  />
{:else if access.status === "unavailable"}
  <p class="state" role="status">
    Rolle und Berechtigungen dieser Mitgliedschaft sind für Sie nicht einsehbar.
  </p>
{:else}
  <div class="form">
    <SectionTitle
      title={tabMeta.title}
      brightBackground={true}
      helpModalTextId={tabMeta.helpDialogId}
      helpModalTooltip={tabMeta.helpDialogTooltip}
    >
      {#snippet buttonSnippet()}
        {#if access.editable}
          <CTA
            type="primaryButton"
            content={access.saving ? "Speichere…" : "Änderungen speichern"}
            onClick={() => void changeMembershipAccess(access)}
            disabled={access.saving || !access.dirty}
            options={{
              label: "Berechtigungsoptionen",
              disabled: access.saving,
              items: [
                {
                  id: "discard",
                  label: "Änderungen verwerfen",
                  icon: mdiRestore,
                  disabled: access.saving || !access.dirty,
                  onClick: () => access.discard(),
                },
              ],
            }}
          >
            {#snippet iconSnippet()}
              <Icon path={mdiContentSave} />
            {/snippet}
          </CTA>
        {/if}
      {/snippet}
    </SectionTitle>

    {#if activeTab === "general"}
      <ActionBox label="Rolle">
        <div class="access-fields">
          <Input
            brightBackground={true}
            type="select"
            placeholder="Rolle"
            tooltip={roleTooltip}
            options={access.roleOptions}
            value={access.draftRoleId}
            clearable={false}
            disabled={!access.roleEditable || access.saving}
            onValueChange={(value) => {
              if (value) access.selectRole(String(value))
            }}
          />
          <Input
            brightBackground={true}
            type="select"
            placeholder="Status"
            tooltip="Gesperrte Mitglieder können diese Organisation nicht mehr nutzen. Änderungen werden erst nach „Speichern“ wirksam."
            options={STATUS_OPTIONS}
            value={access.draftStatus}
            clearable={false}
            disabled={!access.statusEditable || access.saving}
            onValueChange={(value) => {
              if (value === "active" || value === "suspended") access.selectStatus(value)
            }}
          />
        </div>
      </ActionBox>
    {/if}

    {#if !access.canViewRoles}
      <p class="state" role="status"
        >Die Berechtigungen dieser Rolle sind für Sie nicht einsehbar.</p
      >
    {:else if roleState?.status === "error"}
      <UserTabState
        error="Berechtigungen der Rolle konnten nicht geladen werden."
        userPresent={true}
        onRetry={() => {
          if (access.draftRoleId) void access.loadRole(access.draftRoleId)
        }}
      />
    {:else if !roleState?.role}
      <UserTabState loading={true} loadingLabel="Berechtigungen werden geladen…" />
    {:else if sections.length}
      <PermissionsGrid {sections} levels={grid.levels} scopes={grid.scopes} readonly={true} />
    {/if}
  </div>
{/if}

<style lang="less">
  .form {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
  }

  .access-fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
    padding-top: 0.4rem;
  }

  .state {
    margin: 0;
    color: var(--text-invers-100);
  }

  @media (max-width: 720px) {
    .access-fields {
      grid-template-columns: 1fr;
    }
  }
</style>
